import gsap from 'gsap'
import { getAscendantSign } from './astro.js'

// ── BLUEPRINT DATA ────────────────────────────────────────────────────────────

const blueprintMeanings = {
    "n-lifepath":   "LIFE PATH - Your primary life direction, innate talents, and the mission you were born to fulfill.",
    "n-expression": "EXPRESSION — How you manifest your potential in the world, your hidden abilities, and your path to success.",
    "n-soulurge":   "SOUL URGE — Your heart's true desire: the inner motivation that drives your soul's deepest yearnings.",
    "asc-name":     "ASCENDANT — Your social mask: the outward persona and first impression you project to the world."
}

const numberMeanings = {
    1: "The Pioneer: Independence, leadership, and new beginnings.",
    2: "The Partner: Diplomacy, sensitivity, and cooperation.",
    3: "The Artist: Creativity, self-expression, and social joy.",
    4: "The Builder: Discipline, stability, and hard work.",
    5: "The Rebel: Freedom, adventure, and adaptability.",
    6: "The Caretaker: Responsibility, love, and harmony.",
    7: "The Seeker: Analysis, spirituality, and inner wisdom.",
    8: "The Powerhouse: Ambition, authority, and financial mastery.",
    9: "The Humanitarian: Compassion, completion, and idealism.",
    11: "The Visionary: Intuition, enlightenment, and spiritual teaching.",
    22: "The Master Architect: Turning grand visions into reality."
}

// ── NUMEROLOGY HELPERS (private to auth module) ─────────────────────────────

const numerologyMap = {
    a:1, i:9, j:1, q:8, y:7, b:2, k:2, r:9, c:3, l:3, s:1, d:4, m:4, t:2,
    e:5, n:5, u:3, f:6, o:6, v:4, g:7, p:7, w:5, h:8, x:6, z:8,
    // CYRILLIC
    а:1, и:1, й:1, ь:1, ы:1, і:1,
    б:2, к:2, р:2, я:2,
    в:3, л:3, с:3, щ:3, г:3, ґ:3,
    м:4, т:4, э:4, є:4,
    д:5, н:5, у:5, ю:5,
    е:6, о:6, х:6, ё:6,
    п:7, ф:7,
    ж:8, ш:8,
    з:9, р:9, ц:9, ї:9
}

function _getNumberSum(str) {
    const cleanStr = str.toLowerCase().replace(/[^a-zа-яёіїєґ]/g, '')
    return cleanStr.split('').reduce((acc, char) => acc + (numerologyMap[char] || 0), 0)
}

function _getVowelsSum(str) {
    const vowelsList = 'aeiouyаеёиоуыэюяіїє'
    const cleanStr = str.toLowerCase().split('').filter(char => vowelsList.includes(char))
    return cleanStr.reduce((acc, char) => acc + (numerologyMap[char] || 0), 0)
}

function _reduceNum(num, isMaster = true) {
    if (isMaster && (num === 11 || num === 22)) return num
    while (num > 9) {
        num = String(num).split('').reduce((a, b) => +a + +b, 0)
        if (isMaster && (num === 11 || num === 22)) return num
    }
    return num
}

function _getAge(dateString) {
    const today = new Date()
    const birthDate = new Date(dateString)
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--
    return age
}

// ── MAIN EXPORT ──────────────────────────────────────────────────────────────

export function initAuth(supabaseClient, {
    sounds,
    checkLimit,
    updateButtonStates,
    showMagicAlert,
    setCurrentUser,      // (user) => void  — syncs back to script.js
    setUserProfileData,  // (data) => void  — syncs back to script.js
    getCurrentUser,      // () => user      — reads script.js value
    getUserProfileData,  // () => data      — reads script.js value
}) {
    // Internal setters — update both auth module AND script.js
    function _setUser(user) { setCurrentUser(user) }
    function _setProfile(data) { setUserProfileData(data) }

    // ── BLUEPRINT HELPERS ─────────────────────────────────────────────────

    function initBlueprintTooltips() {
        let tooltip = document.getElementById('blueprint-helper')
        if (!tooltip) {
            tooltip = document.createElement('div')
            tooltip.id = 'blueprint-helper'
            tooltip.className = 'blueprint-tooltip'
            document.body.appendChild(tooltip)
        }

        Object.keys(blueprintMeanings).forEach(id => {
            const target = document.getElementById(id)
            if (!target) return

            const triggerArea = target.closest('.n-badge') || target.parentElement

            triggerArea.addEventListener('mouseenter', () => {
                const currentNum = parseInt(target.innerText)
                let content = `<strong>${blueprintMeanings[id]}</strong>`
                if (!isNaN(currentNum) && numberMeanings[currentNum]) {
                    content += `<br><br><span style="color: #fff;">Your Number ${currentNum}:</span><br><em>${numberMeanings[currentNum]}</em>`
                }
                tooltip.innerHTML = content
                tooltip.classList.add('active')
            })

            triggerArea.addEventListener('mousemove', (e) => {
                tooltip.style.left = `${e.clientX - tooltip.offsetWidth / 2}px`
                tooltip.style.top  = `${e.clientY - tooltip.offsetHeight - 20}px`
            })

            triggerArea.addEventListener('mouseleave', () => tooltip.classList.remove('active'))
        })
    }

    function createGoldenWheel(lp, exp, su) {
        const container = document.getElementById('blueprint-wheel-svg')

        // Kill any running infinite tweens from a previous call
        gsap.killTweensOf(["#main-rotator", ".number-node", ".orbit-outer", ".orbit-inner",
                            "#energy-triangle", "#txt-lp", "#txt-exp", "#txt-su",
                            ".particle", ".magic-wheel-svg"])

        let particles = ''
        for (let i = 0; i < 15; i++) {
            const x    = Math.random() * 200
            const y    = Math.random() * 200
            const size = Math.random() * 3 + 1
            if (i % 3 === 0) {
                particles += `<path class="particle" d="M${x} ${y} L${x+size} ${y+size*1.5} L${x-size} ${y+size*1.5} Z" fill="gold" opacity="0.6" />`
            } else {
                particles += `<circle class="particle" cx="${x}" cy="${y}" r="${size/2}" fill="gold" opacity="0.6" />`
            }
        }

        container.innerHTML = `
        <svg viewBox="0 0 200 200" class="magic-wheel-svg" style="overflow: visible;">
            <defs>
                <filter id="gold-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
            </defs>
            <g id="particles-layer">${particles}</g>
            <circle cx="100" cy="100" r="90" fill="none" stroke="gold" stroke-width="0.2" opacity="0.1" />
            <circle class="orbit-outer" cx="100" cy="100" r="80" fill="none" stroke="gold" stroke-width="0.5" stroke-dasharray="8 15" opacity="0.3" />
            <circle class="orbit-inner" cx="100" cy="100" r="55" fill="none" stroke="gold" stroke-width="0.4" stroke-dasharray="4 10" opacity="0.2" />
            <g id="main-rotator">
                <path id="energy-triangle" d="M100 55 L140 125 L60 125 Z" fill="none" stroke="gold" stroke-width="0.5" opacity="0.4" stroke-dasharray="3 3" />
                <g class="number-node" id="node-lp" transform="translate(100, 55)">
                    <circle r="16" fill="rgba(10,10,10,0.9)" stroke="gold" stroke-width="0.8" />
                    <text id="txt-lp" text-anchor="middle" y="6" fill="#fed907" font-size="18" font-weight="bold" filter="url(#gold-glow)">${lp}</text>
                    <text y="32" text-anchor="middle" fill="gold" font-size="10" font-weight="300" opacity="0.9" style="letter-spacing: 0.5px;">LIFE PATH</text>
                </g>
                <g class="number-node" id="node-exp" transform="translate(140, 125)">
                    <circle r="13" fill="rgba(10,10,10,0.9)" stroke="gold" stroke-width="0.6" />
                    <text id="txt-exp" text-anchor="middle" y="5" fill="#fed907" font-size="14" filter="url(#gold-glow)">${exp}</text>
                    <text y="28" text-anchor="middle" fill="gold" font-size="10" font-weight="300" opacity="0.8">EXPRESSION</text>
                </g>
                <g class="number-node" id="node-su" transform="translate(60, 125)">
                    <circle r="13" fill="rgba(10,10,10,0.9)" stroke="gold" stroke-width="0.6" />
                    <text id="txt-su" text-anchor="middle" y="5" fill="#fed907" font-size="14" filter="url(#gold-glow)">${su}</text>
                    <text y="28" text-anchor="middle" fill="gold" font-size="10" font-weight="300" opacity="0.8">SOUL URGE</text>
                </g>
            </g>
        </svg>`

        gsap.timeline().fromTo(".magic-wheel-svg",
            { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.5, ease: "power2.out" })

        gsap.to("#main-rotator",  { rotation: 360,  duration: 30, repeat: -1, ease: "none", transformOrigin: "100px 100px" })
        gsap.to(".number-node",   { rotation: -360, duration: 30, repeat: -1, ease: "none", transformOrigin: "center center" })
        gsap.to(".orbit-outer",   { rotation: 360,  duration: 50, repeat: -1, ease: "none", transformOrigin: "center" })
        gsap.to(".orbit-inner",   { rotation: -360, duration: 40, repeat: -1, ease: "none", transformOrigin: "center" })

        gsap.utils.toArray(".particle").forEach(particle => {
            gsap.to(particle, {
                x: "+=" + (Math.random() * 40 - 20),
                y: "+=" + (Math.random() * 40 - 20),
                opacity: Math.random(),
                duration: 2 + Math.random() * 3,
                repeat: -1, yoyo: true, ease: "sine.inOut"
            })
        })

        gsap.to("#energy-triangle", { opacity: 0.6, strokeWidth: 1, duration: 2, repeat: -1, yoyo: true })
        gsap.to(["#txt-lp", "#txt-exp", "#txt-su"], {
            scale: 1.1, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut", transformOrigin: "center"
        })
    }

    function calculateArcane(dateString) {
        if (!dateString) return { personality: null, soul: null }
        const parts = dateString.split(/[\.-]/)
        let day, month, year
        if (parseInt(parts[0]) > 1000) {
            [year, month, day] = parts.map(Number)
        } else {
            [day, month, year] = parts.map(Number)
        }
        function reduceByDigits(num, limit) {
            let result = num
            while (result > limit) {
                result = result.toString().split('').map(Number).reduce((a, b) => a + b, 0)
            }
            return result
        }
        const totalSum = day + month + year
        let personality = reduceByDigits(totalSum, 22)
        if (personality === 0) personality = 22
        const soul = reduceByDigits(personality, 9)
        return { personality, soul }
    }

    async function updateTimezoneFromCoords(lat, lng) {
        const timestamp = Math.floor(Date.now() / 1000)
        const apiKey    = import.meta.env.VITE_GOOGLE_MAPS_KEY
        const tzUrl     = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`
        try {
            const response = await fetch(tzUrl)
            const data     = await response.json()
            if (data.status === 'OK') {
                window.userUtcOffset = (data.rawOffset + data.dstOffset) / 3600
            }
        } catch (e) {
            console.error('Error getting UTC offset:', e)
        }
    }

    function initAutocompleteHandler() {
        const input = document.getElementById('city-autocomplete')
        if (!input) return
        const tryInit = setInterval(() => {
            if (window.google?.maps?.places) {
                clearInterval(tryInit)
                const autocomplete = new google.maps.places.Autocomplete(input, {
                    types:  ['(cities)'],
                    fields: ['geometry', 'name']
                })
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace()
                    if (place?.geometry?.location) {
                        window.userLat = place.geometry.location.lat()
                        window.userLng = place.geometry.location.lng()
                        await updateTimezoneFromCoords(window.userLat, window.userLng)
                    }
                })
            }
        }, 300)
    }

    // ── AUTH STATE ────────────────────────────────────────────────────────

    async function checkUser() {
        const { data: { user } } = await supabaseClient.auth.getUser()
        _setUser(user)
        updateAuthUI(user)
    }

    function updateAuthUI(user) {
        const authBtn = document.getElementById('auth-btn')
        const authImg = document.getElementById('auth-img')
        if (!authBtn || !authImg) return

        if (user) {
            authImg.src = './icon_history.png'
            authBtn.onclick = showHistory
            authBtn.title = 'Chronicles'
            loadUserProfile(user.id)
        } else {
            authImg.src = './icon_auth.png'
            authBtn.onclick = signInWithGoogle
            authBtn.title = 'Login'
        }
    }

    supabaseClient.auth.onAuthStateChange((event, session) => {
        console.log('Auth event detected:', event)
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
            _setUser(session?.user)
            updateAuthUI(session?.user)
        } else if (event === 'SIGNED_OUT') {
            _setUser(null)
            updateAuthUI(null)
        }
    })

    async function signInWithGoogle() {
        await supabaseClient.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: window.location.origin }
        })
    }

    async function handleLogout() {
        sounds.click.play()
        const { error } = await supabaseClient.auth.signOut()
        if (error) { console.error('Error logging out:', error.message); return }
        _setUser(null)
        document.getElementById('history-overlay').style.display = 'none'
        checkUser()
    }

    // ── PROFILE RENDER ────────────────────────────────────────────────────

    function renderProfileUI(data) {
        if (!data) return

        const fields = {
            'profile-first-name':  data.first_name,
            'profile-last-name':   data.last_name,
            'profile-gender':      data.gender,
            'birth-date-input':    data.birth_date,
            'profile-birth-time':  data.birth_time,
            'profile-birth-place': data.birth_place
        }
        Object.keys(fields).forEach(id => {
            const el = document.getElementById(id)
            if (el && fields[id]) el.value = fields[id]
        })

        const nameDisplay = document.getElementById('user-name-display')
        if (nameDisplay) nameDisplay.innerText = data.first_name || 'Mysterious Seeker'

        if (data.num_life_path) {
            document.getElementById('n-lifepath').innerText             = data.num_life_path
            document.getElementById('n-expression').innerText           = data.num_expression || '?'
            document.getElementById('n-soulurge').innerText             = data.num_soul_urge || '?'
            document.getElementById('asc-name').innerText               = data.ascendant || 'Unknown'
            document.getElementById('blueprint-summary-text').innerText = data.blueprint_summary || ''
            createGoldenWheel(data.num_life_path, data.num_expression || 0, data.num_soul_urge || 0)
            document.getElementById('personality-arcane').style.display = 'block'
        }
    }

    // ── LOAD PROFILE ──────────────────────────────────────────────────────

    async function loadUserProfile(userId, forcedData = null) {
        if (!userId || userId === 'undefined') {
            console.warn('Попытка загрузить профиль с пустым ID, ждем авторизации...')
            return
        }
        try {
            let profile = forcedData

            if (!profile) {
                const { data, error } = await supabaseClient
                    .from('profiles').select('*').eq('id', userId).maybeSingle()
                if (error) throw error
                profile = data
            }

            if (!profile) return
            _setProfile(profile)

            renderProfileUI(profile)

            if (typeof checkLimit === 'function') {
                checkLimit()
                updateButtonStates()
            }

            const coinsDisplay = document.getElementById('user-coins-display')
            if (coinsDisplay) {
                coinsDisplay.innerText = (profile.coins !== undefined && profile.coins !== null) ? profile.coins : 0
            }

            const subCheckbox = document.getElementById('subscribe-checkbox')
            if (subCheckbox) subCheckbox.checked = profile.is_subscribed === true

            const emailDisplay = document.getElementById('user-email-display')
            const currentUser = getCurrentUser()
            if (emailDisplay && currentUser) emailDisplay.innerText = currentUser.email

            const nameDisplay = document.getElementById('user-name-display')
            if (nameDisplay) nameDisplay.innerText = profile.first_name || 'Mysterious Seeker'

            if (profile.num_life_path) {
                const lpElem  = document.getElementById('n-lifepath')
                const exElem  = document.getElementById('n-expression')
                const suElem  = document.getElementById('n-soulurge')
                const ascElem = document.getElementById('asc-name')
                const sumElem = document.getElementById('blueprint-summary-text')

                if (lpElem)  lpElem.innerText  = profile.num_life_path
                if (exElem)  exElem.innerText  = profile.num_expression || '?'
                if (suElem)  suElem.innerText  = profile.num_soul_urge || '?'
                if (ascElem) ascElem.innerText = profile.ascendant || 'Unknown'
                if (sumElem) sumElem.innerText = profile.blueprint_summary || ''

                createGoldenWheel(profile.num_life_path, profile.num_expression || 0, profile.num_soul_urge || 0)
                setTimeout(initBlueprintTooltips, 500)

                const personalityBlock = document.getElementById('personality-arcane')
                if (personalityBlock) personalityBlock.style.display = 'block'
            }

            if (profile.personality_arcane !== null) {
                const pId = profile.personality_arcane
                let sId   = profile.soul_arcane || pId
                if (sId === null || sId === undefined) {
                    sId = pId
                    while (sId > 9 && sId !== 19) {
                        sId = sId.toString().split('').map(Number).reduce((a, b) => a + b, 0)
                    }
                }

                const idsToFetch = [...new Set([pId, sId])]
                const { data: arcanaData } = await supabaseClient
                    .from('arcana_info').select('*').in('id', idsToFetch)

                console.log('DEBUG: Arcana Data from Supabase:', arcanaData)

                if (arcanaData) {
                    const pInfo = arcanaData.find(a => a.id === pId)
                    const sInfo = arcanaData.find(a => a.id === sId)

                    if (pInfo) {
                        document.getElementById('p-name').innerText = pInfo.name
                        document.getElementById('p-desc').innerText = pInfo.description
                    }

                    const soulBadge = document.getElementById('soul-badge')
                    if (sId !== pId && sInfo) {
                        soulBadge.style.display = 'flex'
                        document.getElementById('s-name').innerText = sInfo.name
                        document.getElementById('s-desc').innerText = sInfo.description
                    } else {
                        soulBadge.style.display = 'none'
                    }

                    document.getElementById('personality-arcane').style.display = 'block'
                    document.getElementById('birthdate-section').style.display  = 'none'

                    document.querySelectorAll('.arcana-badge').forEach(badge => {
                        badge.onclick = (e) => {
                            e.stopPropagation()
                            const desc = badge.querySelector('.badge-desc')
                            desc.style.display = desc.style.display === 'block' ? 'none' : 'block'
                        }
                    })
                }
            }
        } catch (e) {
            console.error('Error loading interactive profile:', e)
        }
    }

    // ── BLUEPRINT / SAVE ──────────────────────────────────────────────────

    function resetSaveButton() {
        const btn = document.getElementById('save-profile-btn')
        btn.innerText = 'SCAN MY DESTINY'
        btn.disabled  = false
    }

    async function calculateAndSaveBlueprint() {
        let currentUser = getCurrentUser()
        if (!currentUser) {
            const { data: { session } } = await supabaseClient.auth.getSession()
            if (session) { _setUser(session.user); currentUser = session.user }
            else { await showMagicAlert('Please log in again to save your blueprint.'); return }
        }

        const cityName = document.getElementById('city-autocomplete').value
        if (!cityName) {
            await showMagicAlert('Please select your city.')
            return resetSaveButton()
        }

        const service = new google.maps.places.PlacesService(document.createElement('div'))
        const place = await new Promise((resolve) => {
            service.findPlaceFromQuery({
                query:  cityName,
                fields: ['name', 'geometry'],
            }, (results, status) => {
                resolve(status === google.maps.places.PlacesServiceStatus.OK ? results[0] : null)
            })
        })

        if (!place || !place.geometry) {
            await showMagicAlert('City not found. Please select from the list.')
            return resetSaveButton()
        }

        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        await updateTimezoneFromCoords(lat, lng)
        const tzOffset = window.userUtcOffset || 0

        const currentYear = new Date().getFullYear()
        const btn = document.getElementById('save-profile-btn')
        const originalText = btn.innerText
        btn.innerText = 'SCANNING...'
        btn.disabled  = true

        const firstName = document.getElementById('profile-first-name').value.trim()
        const lastName  = document.getElementById('profile-last-name').value.trim()
        const fullName  = `${firstName} ${lastName}`.trim()
        const birthDate = document.getElementById('birth-date-input').value.trim()
        const birthTime = document.getElementById('profile-birth-time').value.trim()
        const gender    = document.getElementById('profile-gender').value

        if (!firstName || !lastName || !birthDate || !birthTime || !cityName || !gender) {
            await showMagicAlert('The Oracle needs all details to weave your path.')
            return resetSaveButton()
        }

        const [d, m, y] = birthDate.split('.').map(Number)
        const Birthyear = new Date(y, m - 1, d).getFullYear()

        if (Birthyear < 1900 || Birthyear > currentYear) {
            await showMagicAlert(`The year ${Birthyear} is outside the Oracle's reach. Please enter a year between 1900 and ${currentYear}.`)
            return resetSaveButton()
        }

        if (_getAge(birthDate) < 18) {
            await showMagicAlert("The Oracle's wisdom is reserved for those who have reached 18 solar cycles. Access denied.")
            return resetSaveButton()
        }

        const arcanes      = calculateArcane(birthDate)
        const lpRaw        = birthDate.replace(/\D/g, '').split('').reduce((a, b) => +a + +b, 0)
        const lifePath     = _reduceNum(lpRaw)
        const expression   = _reduceNum(_getNumberSum(fullName))
        const soulUrge     = _reduceNum(_getVowelsSum(fullName))
        const myAscendant  = getAscendantSign(birthDate, birthTime, tzOffset, lat, lng)
        console.log('DEBUG: Асцендент рассчитан:', myAscendant)

        const [day, month, year] = birthDate.split('.')
        const formattedBirthDate = `${year}-${month}-${day}`

        const updateData = {
            first_name: firstName,  last_name: lastName,     full_name: fullName,
            birth_date: formattedBirthDate,                  birth_time: birthTime,
            birth_place: cityName,  lat, lon: lng,           gender,
            personality_arcane: arcanes.personality,         soul_arcane: arcanes.soul,
            num_life_path: lifePath, num_expression: expression, num_soul_urge: soulUrge,
            ascendant: myAscendant,
            blueprint_summary: `Your Destiny Code is ${lifePath}-${expression}-${soulUrge}. Ascendant: ${myAscendant}.`
        }

        try {
            const { error } = await supabaseClient.from('profiles').update(updateData).eq('id', currentUser.id)
            if (error) throw error

            const merged = { ...getUserProfileData(), ...updateData }
            _setProfile(merged)
            renderProfileUI(merged)
            if (currentUser?.id) await loadUserProfile(currentUser.id)

            createGoldenWheel(lifePath, expression, soulUrge)
            await showMagicAlert('Your destiny has been scanned and updated!')
        } catch (err) {
            console.error('Error saving blueprint:', err)
        } finally {
            btn.innerText = originalText
            btn.disabled  = false
        }
    }

    function initProfileActions() {
        const saveProfileBtn = document.getElementById('save-profile-btn')
        if (saveProfileBtn) {
            saveProfileBtn.removeEventListener('click', calculateAndSaveBlueprint)
            saveProfileBtn.addEventListener('click', calculateAndSaveBlueprint)
        } else {
            console.error("Кнопка 'save-profile-btn' не найдена в DOM!")
        }
    }

    // ── HISTORY ───────────────────────────────────────────────────────────

    function renderHistoryUI(readings) {
        const list    = document.getElementById('history-list')
        const overlay = document.getElementById('history-overlay')
        list.innerHTML = ''

        if (readings.length === 0) {
            list.innerHTML = '<p style="color:gray; text-align:center;">The scrolls are empty yet...</p>'
        }

        readings.forEach(item => {
            const date = new Date(item.created_at).toLocaleDateString('en-US')
            const div  = document.createElement('div')
            div.className = 'history-item'
            div.innerHTML = `
                <div class="history-date">${date}</div>
                <div class="history-question">"${item.question}"</div>
                <div class="history-answer">${item.prediction}</div>
            `
            list.appendChild(div)
        })

        overlay.style.display = 'flex'
    }

    async function showHistory() {
        sounds.click.play()
        const currentUser = getCurrentUser()
        if (!currentUser) return

        const { data: readings, error } = await supabaseClient
            .from('readings').select('*')
            .eq('user_id', currentUser.id)
            .order('created_at', { ascending: false })

        if (error) { console.error(error); return }
        renderHistoryUI(readings)
    }

    // ── DATE PICKERS ──────────────────────────────────────────────────────

    function initMagicPickers() {
        const today = new Date()

        flatpickr('#birth-date-input', {
            dateFormat: 'd.m.Y',
            altInput: true,
            altFormat: 'd.m.Y',
            allowInput: true,
            minDate: '01.01.1900',
            maxDate: today,
            disableMobile: true,
            onReady(_selectedDates, _dateStr, instance) {
                const altInput      = instance.altInput
                const originalInput = instance.element
                const flexRow       = originalInput.parentNode
                flexRow.insertBefore(altInput, originalInput.nextSibling)

                altInput.removeAttribute('readonly')
                altInput.placeholder     = 'dd.mm.yyyy'
                altInput.style.flex      = '1'
                altInput.style.minWidth  = '0'
                altInput.style.width     = '100%'
                altInput.style.boxSizing = 'border-box'

                altInput.addEventListener('click', () => instance.open())
                altInput.addEventListener('focus', () => instance.open())
                altInput.addEventListener('input', (e) => {
                    let value = e.target.value.replace(/\D/g, '')
                    if      (value.length > 2 && value.length <= 4) value = value.slice(0, 2) + '.' + value.slice(2)
                    else if (value.length > 4)                       value = value.slice(0, 2) + '.' + value.slice(2, 4) + '.' + value.slice(4, 8)
                    e.target.value = value
                })
            },
            onClose(_selectedDates, _dateStr, instance) {
                instance.setDate(instance.altInput.value, true, 'd.m.Y')
            }
        })

        flatpickr('#profile-birth-time', {
            enableTime: true, noCalendar: true,
            dateFormat: 'H:i', time_24hr: true,
            disableMobile: true, allowInput: true,
            static: true, minuteIncrement: 1
        })
    }

    // ── EVENT LISTENERS ───────────────────────────────────────────────────

    document.getElementById('edit-profile-btn').onclick = function() {
        const birthdateSection = document.getElementById('birthdate-section')
        birthdateSection.style.display = 'block'
        document.getElementById('personality-arcane').style.display = 'none'
        document.getElementById('save-profile-btn').innerText = 'Recalculate Destiny'
        birthdateSection.scrollIntoView({ behavior: 'smooth' })
    }

    document.getElementById('logout-btn').addEventListener('click', handleLogout)

    document.addEventListener('DOMContentLoaded', initProfileActions)

    document.addEventListener('click', (e) => {
        if (e.target.id === 'save-profile-btn') calculateAndSaveBlueprint()
    })

    document.addEventListener('change', async (event) => {
        if (event.target?.id !== 'subscribe-checkbox') return
        const currentUser = getCurrentUser()
        if (!currentUser) return
        const isChecked = event.target.checked
        try {
            const { error } = await supabaseClient.from('profiles')
                .update({ is_subscribed: isChecked }).eq('id', currentUser.id)
            if (error) throw error
            const profile = getUserProfileData()
            if (profile) profile.is_subscribed = isChecked
        } catch (err) {
            console.error('Error updating subscription:', err.message)
        }
    })

    document.getElementById('birth-date-input').addEventListener('input', (e) => {
        let value = e.target.value.replace(/\D/g, '')
        if      (value.length > 2 && value.length <= 4) value = value.slice(0, 2) + '.' + value.slice(2)
        else if (value.length > 4)                       value = value.slice(0, 2) + '.' + value.slice(2, 4) + '.' + value.slice(4, 8)
        e.target.value = value
    })

    document.addEventListener('DOMContentLoaded', initMagicPickers)

    // ── INIT ──────────────────────────────────────────────────────────────

    initAutocompleteHandler()
    checkUser()

    return { checkUser, signInWithGoogle, handleLogout, loadUserProfile, showHistory, initMagicPickers }
}
